import { useState, useEffect, useMemo, Fragment } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import API from '../../utils/api';
import PageHeader from '../../components/common/PageHeader';
import EmptyState from '../../components/common/EmptyState';
import Badge from '../../components/common/Badge';
import Modal from '../../components/common/Modal';
import {
  HiOutlinePlus,
  HiOutlineUsers,
  HiOutlineX,
  HiOutlineCheck,
  HiOutlineExclamation,
  HiOutlineCalendar,
  HiOutlineRefresh,
  HiOutlineFilter,
  HiOutlineChevronDown,
  HiOutlineChevronUp,
} from 'react-icons/hi';

const STATUS_STYLES = {
  present:   { variant: 'success', label: 'উপস্থিত', icon: '✓', color: 'emerald' },
  absent:    { variant: 'danger',  label: 'অনুপস্থিত', icon: '✕', color: 'rose' },
  half_day:  { variant: 'warning', label: 'আধা দিন',  icon: '◐', color: 'amber' },
};

// Format a Date as YYYY-MM-DD in LOCAL time (avoids UTC shift bugs).
const fmtLocal = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// Convert a YYYY-MM-DD string to a local Date at midnight.
const parseDate = (s) => {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
};

/**
 * Group an array of attendance rows into week buckets.
 * Week starts on Saturday (Bangladesh convention, Sat–Thu work week).
 * Returns [{ weekStart, weekEnd, rows }] in chronological order.
 */
function groupByWeek(rows) {
  const groups = [];
  const map = new Map();
  for (const a of rows) {
    const date = parseDate(a.date);
    const diff = (date.getDay() + 1) % 7; // Sat→0, Sun→1, ... Fri→6
    const sat = new Date(date);
    sat.setDate(date.getDate() - diff);
    const key = fmtLocal(sat);
    if (!map.has(key)) {
      const end = new Date(sat);
      end.setDate(sat.getDate() + 6);
      const item = { weekStart: key, weekEnd: fmtLocal(end), rows: [] };
      map.set(key, item);
      groups.push(item);
    }
    map.get(key).rows.push(a);
  }
  return groups;
}

/**
 * Build the (start, end) date pair for a given preset.
 * - 'today'    → today / today
 * - 'yesterday'→ yesterday / yesterday
 * - 'thisWeek' → Monday / Sunday of current ISO week
 * - 'lastWeek' → Monday / Sunday of previous ISO week
 * - 'thisMonth'→ 1st / last day of current month
 * - 'lastMonth'→ 1st / last day of previous month
 * - 'last7'    → today-6 / today  (rolling 7-day window)
 * - 'last30'   → today-29 / today (rolling 30-day window)
 */
function rangeForPreset(preset) {
  const today = new Date();
  const fmt = (d) => d.toISOString().split('T')[0];

  // Helper: get the most recent Saturday (week start)
  const getSaturday = (d) => {
    const dow = d.getDay(); // 0=Sun, 1=Mon, ..., 6=Sat
    const diff = (dow + 1) % 7; // Sat→0, Sun→1, ... Fri→6
    const sat = new Date(d);
    sat.setDate(d.getDate() - diff);
    return sat;
  };

  if (preset === 'today') return [fmt(today), fmt(today)];

  if (preset === 'thisWeek') {
    // Saturday → Today
    const start = getSaturday(today);
    return [fmt(start), fmt(today)];
  }

  if (preset === 'lastWeek') {
    // Last Saturday → Last Friday
    const thisSat = getSaturday(today);
    const lastSat = new Date(thisSat);
    lastSat.setDate(thisSat.getDate() - 7);
    const lastFri = new Date(lastSat);
    lastFri.setDate(lastSat.getDate() + 6);
    return [fmt(lastSat), fmt(lastFri)];
  }

  if (preset === 'thisMonth' || preset === 'lastMonth') {
    const d = new Date(today);
    if (preset === 'lastMonth') d.setMonth(d.getMonth() - 1);
    const start = new Date(d.getFullYear(), d.getMonth(), 1);
    const end = new Date(d.getFullYear(), d.getMonth() + 1, 0);
    return [fmt(start), fmt(end)];
  }

  if (preset === 'last7') {
    const start = new Date(today); start.setDate(start.getDate() - 6);
    return [fmt(start), fmt(today)];
  }

  if (preset === 'last30') {
    const start = new Date(today); start.setDate(start.getDate() - 29);
    return [fmt(start), fmt(today)];
  }

  return [fmt(today), fmt(today)];
}

const RANGE_PRESETS = [
  { value: 'today',      label: 'আজ' },
  { value: 'yesterday',  label: 'গতকাল' },
  { value: 'last7',      label: 'গত ৭ দিন' },
  { value: 'thisWeek',   label: 'এই সপ্তাহ' },
  { value: 'lastWeek',   label: 'গত সপ্তাহ' },
  { value: 'thisMonth',  label: 'এই মাস' },
  { value: 'lastMonth',  label: 'গত মাস' },
  { value: 'last30',     label: 'গত ৩০ দিন' },
];

const AttendancePage = () => {
  const [workers, setWorkers] = useState([]);
  const [sites, setSites] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(false);

  // Single-mark form (existing)
  const [formData, setFormData] = useState({
    worker_id: '', site_id: '', date: new Date().toISOString().split('T')[0], status: 'present'
  });

  // ── Filter state ────────────────────────────────────────────────────────
  // Range preset (or 'custom' to use the manual date inputs)
  const [rangePreset, setRangePreset] = useState('thisMonth');
  const today = new Date().toISOString().split('T')[0];
  const [customStart, setCustomStart] = useState(rangeForPreset('thisMonth')[0]);
  const [customEnd,   setCustomEnd]   = useState(rangeForPreset('thisMonth')[1]);

  // Worker filter — empty string means "all"
  const [filterWorkerId, setFilterWorkerId] = useState('');

  // Whether to show the per-worker breakdown panel
  const [showBreakdown, setShowBreakdown] = useState(true);

  // Bulk form
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkData, setBulkData] = useState({
    site_id: '',
    date: new Date().toISOString().split('T')[0],
    status: 'present',
    selectedIds: new Set(),
    includeAdmins: false,
  });
  const [bulkSearch, setBulkSearch] = useState('');
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  // Per-row toggle state for click-to-cycle status badge
  const [togglingId, setTogglingId] = useState(null);

  const { t } = useLanguage();

  // Derive the actual date range from preset / custom state.
  const [rangeStart, rangeEnd] = useMemo(() => {
    if (rangePreset === 'custom') return [customStart, customEnd];
    return rangeForPreset(rangePreset);
  }, [rangePreset, customStart, customEnd]);

  useEffect(() => { fetchWorkersAndSites(); }, []);

  // Whenever the filter range or worker changes, refresh both the history
  // table AND the stats panel. Both endpoints accept start_date/end_date
  // and optional worker_id so we can request them in parallel.
  useEffect(() => {
    fetchAttendance();
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeStart, rangeEnd, filterWorkerId]);

  const fetchWorkersAndSites = async () => {
    try {
      const [workersRes, sitesRes] = await Promise.all([
        API.get('/admin/workers'),
        API.get('/admin/sites'),
      ]);
      setWorkers(workersRes.data.workers);
      setSites(sitesRes.data.sites);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchAttendance = async () => {
    setLoading(true);
    try {
      const params = { start_date: rangeStart, end_date: rangeEnd };
      if (filterWorkerId) {
        // Backend will pick the right column (worker_id vs user_id) based on
        // the owner's role, so passing the same id is safe.
        params.worker_id = filterWorkerId;
        params.user_id = filterWorkerId;
      }
      const { data } = await API.get('/admin/attendance', { params });
      setAttendance(data.attendance);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    setStatsLoading(true);
    try {
      const params = { start_date: rangeStart, end_date: rangeEnd };
      if (filterWorkerId) {
        params.worker_id = filterWorkerId;
        params.user_id = filterWorkerId;
      }
      const { data } = await API.get('/admin/attendance/stats', { params });
      setStats(data);
    } catch (error) {
      console.error('Stats error:', error);
    } finally {
      setStatsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await API.post('/admin/attendance', formData);
      // Refresh both history and stats (single record change affects both)
      fetchAttendance();
      fetchStats();
      setFormData({ worker_id: '', site_id: '', date: today, status: 'present' });
    } catch (error) { alert(error.response?.data?.message || 'Error'); }
  };

  /**
   * Cycle through statuses when admin clicks a badge in the history table.
   * present → absent → half_day → present.
   */
  const cycleStatus = async (row) => {
    const order = ['present', 'absent', 'half_day'];
    const idx = order.indexOf(row.status);
    const nextStatus = order[(idx + 1) % order.length];

    if (togglingId) return;

    setTogglingId(row.id);
    setAttendance((prev) => prev.map((r) => (r.id === row.id ? { ...r, status: nextStatus } : r)));

    try {
      const ownerId = row.user_id || row.worker_id;
      await API.post('/admin/attendance', {
        [row.owner_kind === 'admin' ? 'user_id' : 'worker_id']: ownerId,
        site_id: row.site_id,
        date: row.date,
        status: nextStatus,
      });
      // Stats changed → refresh
      fetchStats();
    } catch (error) {
      setAttendance((prev) => prev.map((r) => (r.id === row.id ? { ...r, status: row.status } : r)));
      alert(error.response?.data?.message || 'হাজিরা আপডেট ব্যর্থ');
    } finally {
      setTogglingId(null);
    }
  };

  const deleteAttendance = async (row) => {
    if (!window.confirm(`${row.worker?.name || row.user?.name || 'এই হাজিরা'} (${row.date}) মুছে ফেলবেন?`)) return;
    try {
      await API.delete(`/admin/attendance/${row.id}`);
      setAttendance((prev) => prev.filter((r) => r.id !== row.id));
      fetchStats();
    } catch (error) {
      alert(error.response?.data?.message || 'মুছে ফেলা যায়নি');
    }
  };

  // ── Bulk handlers ─────────────────────────────────────────────────────
  const openBulk = () => {
    setBulkData({
      site_id: sites[0]?.id || '',
      date: today,
      status: 'present',
      selectedIds: new Set(),
      includeAdmins: false,
    });
    setBulkSearch('');
    setBulkResult(null);
    setBulkOpen(true);
  };

  const toggleWorker = (id) => {
    const next = new Set(bulkData.selectedIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setBulkData({ ...bulkData, selectedIds: next });
  };

  const selectAll = (filteredIds) => {
    const next = new Set(bulkData.selectedIds);
    filteredIds.forEach((id) => next.add(id));
    setBulkData({ ...bulkData, selectedIds: next });
  };

  const deselectAll = (filteredIds) => {
    const next = new Set(bulkData.selectedIds);
    filteredIds.forEach((id) => next.delete(id));
    setBulkData({ ...bulkData, selectedIds: next });
  };

  const filteredWorkers = useMemo(() => {
    const base = bulkData.includeAdmins ? workers : workers.filter((w) => w.role !== 'admin');
    if (!bulkSearch.trim()) return base;
    const q = bulkSearch.toLowerCase();
    return base.filter((w) =>
      w.name.toLowerCase().includes(q) ||
      (w.phone || '').toLowerCase().includes(q) ||
      (w.email || '').toLowerCase().includes(q)
    );
  }, [workers, bulkSearch, bulkData.includeAdmins]);

  const alreadyMarked = useMemo(() => {
    const set = new Set();
    attendance.forEach((a) => {
      if (a.date === bulkData.date && a.site_id === Number(bulkData.site_id)) {
        set.add(a.worker_id || a.user_id);
      }
    });
    return set;
  }, [attendance, bulkData.date, bulkData.site_id]);

  const handleBulkSubmit = async () => {
    if (!bulkData.site_id) return alert('সাইট নির্বাচন করুন।');
    if (bulkData.selectedIds.size === 0) return alert('অন্তত একজন ওয়ার্কার নির্বাচন করুন।');

    if (!confirm(`মোট ${bulkData.selectedIds.size} জনের হাজিরা "${STATUS_STYLES[bulkData.status].label}" হিসেবে নেওয়া হবে। নিশ্চিত?`)) return;

    setBulkSubmitting(true);
    try {
      const { data } = await API.post('/admin/attendance/bulk', {
        user_ids: [...bulkData.selectedIds],
        site_id: Number(bulkData.site_id),
        date: bulkData.date,
        status: bulkData.status,
      });
      setBulkResult(data);
      fetchAttendance();
      fetchStats();
    } catch (error) {
      alert(error.response?.data?.message || 'Error');
    } finally {
      setBulkSubmitting(false);
    }
  };

  // Helpers for stats display
  const fmtPct = (n) => `${Math.max(0, Math.min(100, Number(n) || 0))}%`;

  const workerOptions = useMemo(() =>
    [...workers].sort((a, b) => {
      if (a.role !== b.role) return a.role === 'admin' ? -1 : 1;
      return (a.name || '').localeCompare(b.name || '');
    }),
  [workers]);

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-brand-200 border-t-brand-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <PageHeader
        title={t('attendance')}
        subtitle={`${rangeStart} → ${rangeEnd} · মোট ${attendance.length} টি রেকর্ড`}
        action={
          <button
            onClick={openBulk}
            disabled={workerOptions.filter((w) => w.role !== 'admin').length === 0}
            data-welcome="bulk-attendance"
            className="btn-primary"
            title="একসাথে অনেক ওয়ার্কারের হাজিরা নিন"
          >
            <HiOutlineUsers className="w-5 h-5" />
            <span>বাল্ক হাজিরা</span>
          </button>
        }
      />

      {/* ── Filter bar ───────────────────────────────────────────────────── */}
      <div className="card">
        <div className="flex items-center gap-2 mb-3">
          <HiOutlineFilter className="w-5 h-5 text-ink-500 dark:text-ink-400" />
          <h2 className="text-sm font-semibold text-ink-900 dark:text-white uppercase tracking-wider">
            ফিল্টার
          </h2>
        </div>

        <div className="flex flex-col gap-3">
          {/* Preset range tabs */}
          <div className="flex gap-1.5 p-1 bg-ink-100 dark:bg-ink-800 rounded-xl overflow-x-auto">
            {RANGE_PRESETS.map((p) => (
              <button
                key={p.value}
                type="button"
                onClick={() => setRangePreset(p.value)}
                className={
                  'px-3 py-1.5 rounded-lg text-xs sm:text-sm font-semibold transition-colors whitespace-nowrap ' +
                  (rangePreset === p.value
                    ? 'bg-white dark:bg-ink-700 text-brand-700 dark:text-brand-300 shadow-sm'
                    : 'text-ink-600 dark:text-ink-300 hover:text-ink-900 dark:hover:text-white')
                }
              >
                {p.label}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setRangePreset('custom')}
              className={
                'px-3 py-1.5 rounded-lg text-xs sm:text-sm font-semibold transition-colors whitespace-nowrap ' +
                (rangePreset === 'custom'
                  ? 'bg-white dark:bg-ink-700 text-brand-700 dark:text-brand-300 shadow-sm'
                  : 'text-ink-600 dark:text-ink-300 hover:text-ink-900 dark:hover:text-white')
              }
            >
              কাস্টম
            </button>
          </div>

          {/* Custom date pickers + worker filter */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className={rangePreset === 'custom' ? '' : 'opacity-60'}>
              <label className="input-label text-xs">শুরু</label>
              <input
                type="date"
                value={customStart}
                disabled={rangePreset !== 'custom'}
                max={customEnd || undefined}
                onChange={(e) => setCustomStart(e.target.value)}
                className="input-field text-sm"
              />
            </div>
            <div className={rangePreset === 'custom' ? '' : 'opacity-60'}>
              <label className="input-label text-xs">শেষ</label>
              <input
                type="date"
                value={customEnd}
                disabled={rangePreset !== 'custom'}
                min={customStart || undefined}
                max={today}
                onChange={(e) => setCustomEnd(e.target.value)}
                className="input-field text-sm"
              />
            </div>
            <div>
              <label className="input-label text-xs">ওয়ার্কার / ইউজার</label>
              <select
                value={filterWorkerId}
                onChange={(e) => setFilterWorkerId(e.target.value)}
                className="input-field text-sm"
              >
                <option value="">সব দেখান ({workerOptions.length} জন)</option>
                {workerOptions.map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.role === 'admin' ? '👑 ' : '👷 '}
                    {w.name}
                    {w.role === 'admin' ? ' (Admin)' : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Quick clear + active filter pill */}
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="text-xs text-ink-500 dark:text-ink-400 flex items-center gap-2 flex-wrap">
              <HiOutlineCalendar className="w-4 h-4" />
              <span>
                সক্রিয় রেঞ্জ:&nbsp;
                <strong className="text-ink-900 dark:text-white">{rangeStart}</strong>
                {' → '}
                <strong className="text-ink-900 dark:text-white">{rangeEnd}</strong>
                {stats?.range?.total_days != null && (
                  <span className="ml-2 px-2 py-0.5 rounded-full bg-brand-100 dark:bg-brand-900/40 text-brand-700 dark:text-brand-300 text-[10px] font-semibold">
                    {stats.range.total_days} দিন
                  </span>
                )}
              </span>
              {filterWorkerId && (
                <span className="ml-2 px-2 py-0.5 rounded-full bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300 text-[10px] font-semibold">
                  ফিল্টার: {workerOptions.find((w) => String(w.id) === filterWorkerId)?.name || filterWorkerId}
                </span>
              )}
              {statsLoading && (
                <span className="ml-2 inline-flex items-center gap-1 text-ink-400">
                  <HiOutlineRefresh className="w-3 h-3 animate-spin" />
                  লোড হচ্ছে...
                </span>
              )}
            </div>
            {(rangePreset !== 'thisMonth' || filterWorkerId) && (
              <button
                type="button"
                onClick={() => {
                  setRangePreset('thisMonth');
                  setFilterWorkerId('');
                }}
                className="text-xs font-semibold text-rose-600 dark:text-rose-400 hover:underline"
              >
                ✕ ফিল্টার মুছুন
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── Stats panel ────────────────────────────────────────────────── */}
      {stats && (
        <div className="card">
          <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
            <div>
              <h2 className="text-base font-semibold text-ink-900 dark:text-white flex items-center gap-2">
                <span className="w-1.5 h-6 rounded-full bg-brand-500" />
                হাজিরার সারাংশ
                {filterWorkerId && (
                  <span className="text-xs font-normal text-ink-500 dark:text-ink-400">
                    · শুধু {workerOptions.find((w) => String(w.id) === filterWorkerId)?.name}
                  </span>
                )}
              </h2>
              <p className="text-xs text-ink-500 dark:text-ink-400 mt-1">
                {stats.range.start_date} → {stats.range.end_date} ({stats.range.total_days} দিন)
              </p>
            </div>
          </div>

          {/* Top-line stat cards: present / absent / half_day counts + percentages */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            {(() => {
              const o = stats.overall || { present: 0, absent: 0, half_day: 0 };
              const total = o.present + o.absent + o.half_day;
              const cards = [
                { key: 'present',  label: 'উপস্থিত',   icon: '✓', color: 'emerald', bg: 'bg-emerald-50 dark:bg-emerald-900/20',  border: 'border-emerald-200 dark:border-emerald-800' },
                { key: 'absent',   label: 'অনুপস্থিত', icon: '✕', color: 'rose',    bg: 'bg-rose-50 dark:bg-rose-900/20',        border: 'border-rose-200 dark:border-rose-800' },
                { key: 'half_day', label: 'আধা দিন',   icon: '◐', color: 'amber',   bg: 'bg-amber-50 dark:bg-amber-900/20',      border: 'border-amber-200 dark:border-amber-800' },
              ];
              return cards.map((c) => {
                const cnt = o[c.key] || 0;
                const pct = total > 0 ? Math.round((cnt / total) * 100) : 0;
                return (
                  <div key={c.key} className={`p-4 rounded-2xl border ${c.bg} ${c.border}`}>
                    <div className="flex items-center justify-between">
                      <span className={`text-2xl ${
                        c.color === 'emerald' ? 'text-emerald-600' :
                        c.color === 'rose' ? 'text-rose-600' : 'text-amber-600'
                      }`}>{c.icon}</span>
                      <span className="text-xs font-semibold text-ink-500 dark:text-ink-400">{pct}%</span>
                    </div>
                    <div className="mt-2">
                      <p className="text-2xl font-bold text-ink-900 dark:text-white">{cnt}</p>
                      <p className="text-xs text-ink-500 dark:text-ink-400 mt-0.5">{c.label}</p>
                    </div>
                  </div>
                );
              });
            })()}
            <div className="p-4 rounded-2xl border bg-brand-50 dark:bg-brand-900/20 border-brand-200 dark:border-brand-800">
              <div className="flex items-center justify-between">
                <span className="text-2xl">📊</span>
                <span className="text-xs font-semibold text-brand-700 dark:text-brand-300">
                  {fmtPct(
                    stats.range.total_days > 0
                      ? Math.round(
                          ((stats.overall.present + 0.5 * (stats.overall.half_day || 0)) /
                            Math.max(stats.range.total_days * Math.max(stats.per_owner.length, 1))) * 100
                        )
                      : 0
                  )}
                </span>
              </div>
              <div className="mt-2">
                <p className="text-2xl font-bold text-ink-900 dark:text-white">{stats.total_rows}</p>
                <p className="text-xs text-ink-500 dark:text-ink-400 mt-0.5">মোট রেকর্ড</p>
              </div>
            </div>
          </div>

          {/* Per-worker breakdown */}
          {stats.per_owner && stats.per_owner.length > 0 && (
            <div>
              <button
                type="button"
                onClick={() => setShowBreakdown((v) => !v)}
                className="flex items-center gap-2 text-sm font-semibold text-ink-700 dark:text-ink-300 hover:text-ink-900 dark:hover:text-white transition-colors"
              >
                {showBreakdown ? <HiOutlineChevronUp className="w-4 h-4" /> : <HiOutlineChevronDown className="w-4 h-4" />}
                {filterWorkerId ? 'নির্বাচিত ব্যক্তির ভাঙচুর' : `প্রতি ইউজার ভাঙচুর (${stats.per_owner.length} জন)`}
              </button>
              {showBreakdown && (
                <div className="mt-3 overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-ink-50 dark:bg-ink-900/30">
                      <tr>
                        <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-ink-500 dark:text-ink-400">নাম</th>
                        <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-ink-500 dark:text-ink-400 text-center">উপস্থিত</th>
                        <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-ink-500 dark:text-ink-400 text-center">অনুপস্থিত</th>
                        <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-ink-500 dark:text-ink-400 text-center">আধা দিন</th>
                        <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-ink-500 dark:text-ink-400 text-center">মোট</th>
                        <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-ink-500 dark:text-ink-400 text-center">উপস্থিতি %</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-ink-100 dark:divide-ink-700">
                      {stats.per_owner.map((o) => {
                        const isFiltered = String(o.owner_id) === filterWorkerId && o.owner_kind === (workerOptions.find((w) => String(w.id) === filterWorkerId)?.role || 'worker');
                        return (
                          <tr
                            key={`${o.owner_kind}:${o.owner_id}`}
                            className={
                              (isFiltered ? 'bg-brand-50/50 dark:bg-brand-900/20 ' : '') +
                              'hover:bg-ink-50 dark:hover:bg-ink-900/30 cursor-pointer transition-colors'
                            }
                            onClick={() => setFilterWorkerId(String(o.owner_id))}
                          >
                            <td className="px-3 py-2 font-medium text-ink-900 dark:text-white">
                              <span className="inline-flex items-center gap-1.5">
                                {o.owner_kind === 'admin' && <span className="text-amber-700">👑</span>}
                                <span>{o.owner_name}</span>
                                {isFiltered && <span className="text-[10px] font-bold uppercase text-brand-700 bg-brand-100 dark:bg-brand-900/40 px-1.5 py-0.5 rounded">নির্বাচিত</span>}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-center">
                              <span className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-300 font-semibold">
                                ✓ {o.present}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-center">
                              <span className="inline-flex items-center gap-1 text-rose-700 dark:text-rose-300 font-semibold">
                                ✕ {o.absent}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-center">
                              <span className="inline-flex items-center gap-1 text-amber-700 dark:text-amber-300 font-semibold">
                                ◐ {o.half_day}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-center font-bold text-ink-900 dark:text-white">{o.total}</td>
                            <td className="px-3 py-2 text-center">
                              <div className="inline-flex items-center gap-2">
                                <div className="w-20 h-1.5 bg-ink-100 dark:bg-ink-700 rounded-full overflow-hidden">
                                  <div
                                    className={
                                      o.attendance_pct >= 80 ? 'bg-emerald-500' :
                                      o.attendance_pct >= 50 ? 'bg-amber-500' : 'bg-rose-500'
                                    }
                                    style={{ width: `${o.attendance_pct}%`, height: '100%' }}
                                  />
                                </div>
                                <span className="text-xs font-bold text-ink-900 dark:text-white w-9 text-right">{o.attendance_pct}%</span>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Single-mark form */}
      <div className="card">
        <h2 className="text-base font-semibold text-ink-900 dark:text-white mb-4 flex items-center gap-2">
          <span className="w-1.5 h-6 rounded-full bg-brand-500" />
          {t('markAttendance')}
        </h2>
        <form onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="input-label">{t('workerName') || 'ওয়ার্কার'}</label>
            <select value={formData.worker_id} onChange={(e) => setFormData({ ...formData, worker_id: e.target.value })} className="input-field" required>
              <option value="">— নির্বাচন করুন —</option>
              {workers.filter((w) => w.role !== 'admin').map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
            </select>
          </div>
          <div>
            <label className="input-label">সাইট</label>
            <select value={formData.site_id} onChange={(e) => setFormData({ ...formData, site_id: e.target.value })} className="input-field" required>
              <option value="">— নির্বাচন করুন —</option>
              {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div>
            <label className="input-label">{t('date')}</label>
            <input type="date" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} className="input-field" required />
          </div>
          <div>
            <label className="input-label">{t('status')}</label>
            <select value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })} className="input-field">
              <option value="present">✓ {t('present')}</option>
              <option value="absent">✕ {t('absent')}</option>
              <option value="half_day">◐ {t('halfDay')}</option>
            </select>
          </div>
          <div className="sm:col-span-2 lg:col-span-4">
            <button type="submit" className="btn-primary">
              <span>সংরক্ষণ করুন</span>
            </button>
          </div>
        </form>
      </div>

      {/* History */}
      <div className="card p-0 overflow-hidden">
        <div className="px-5 pt-5 pb-3">
          <h2 className="text-base font-semibold text-ink-900 dark:text-white flex items-center gap-2">
            <span className="w-1.5 h-6 rounded-full bg-accent-500" />
            হাজিরার ইতিহাস
            <span className="text-xs text-ink-400 ml-auto">সর্বশেষ {Math.min(attendance.length, 50)}</span>
          </h2>
        </div>

        {attendance.length === 0 ? (
          <EmptyState icon="📅" title="এই রেঞ্জে কোনো হাজিরা নেই" description="ফিল্টার বদলে আবার দেখুন" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-ink-50 dark:bg-ink-900/30 border-y border-ink-100 dark:border-ink-700">
                <tr>
                  <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-ink-500 dark:text-ink-400">{t('date')}</th>
                  <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-ink-500 dark:text-ink-400">{t('workerName') || 'ওয়ার্কার'}</th>
                  <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-ink-500 dark:text-ink-400">সাইট</th>
                  <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-ink-500 dark:text-ink-400">{t('status')}</th>
                  <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-ink-500 dark:text-ink-400">চিহ্নিত করেছেন</th>
                  <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-ink-500 dark:text-ink-400 text-right">অ্যাকশন</th>
                </tr>
              </thead>
              <tbody>
                {groupByWeek(attendance.slice(0, 50)).map((week, wi) => {
                  const isEven = wi % 2 === 0;
                  const weekBg = isEven
                    ? 'bg-brand-50/80 dark:bg-brand-900/30'
                    : 'bg-amber-50/80 dark:bg-amber-900/20';
                  const rowBg = isEven
                    ? 'bg-white dark:bg-ink-800/40'
                    : 'bg-ink-50/50 dark:bg-ink-900/20';
                  return (
                    <Fragment key={week.weekStart}>
                      {/* ── Week divider row ─────────────────────────────── */}
                      <tr className={weekBg}>
                        <td colSpan={6} className="px-5 py-2">
                          <div className="flex items-center gap-2">
                            <span className="w-1 h-4 rounded-full bg-accent-500" />
                            <span className="text-xs font-bold uppercase tracking-wide text-ink-700 dark:text-ink-200">
                              🗓️ সপ্তাহ
                            </span>
                            <span className="text-xs font-semibold text-ink-500 dark:text-ink-400">
                              {week.weekStart} → {week.weekEnd}
                            </span>
                            <span className="ml-auto text-[10px] font-semibold text-ink-400 dark:text-ink-500">
                              {week.rows.length} টি রেকর্ড
                            </span>
                          </div>
                        </td>
                      </tr>
                      {week.rows.map((a) => {
                        const style = STATUS_STYLES[a.status] || STATUS_STYLES.present;
                        const owner = a.owner || a.user || a.worker;
                        const ownerKind = a.owner_kind || (a.user_id ? 'admin' : 'worker');
                        const isToggling = togglingId === a.id;
                        return (
                          <tr key={a.id} className={`${rowBg} hover:bg-brand-50/70 dark:hover:bg-brand-900/20 transition-colors`}>
                            <td className="px-5 py-3 text-sm font-medium text-ink-900 dark:text-white whitespace-nowrap">{a.date}</td>
                            <td className="px-5 py-3 text-sm text-ink-700 dark:text-ink-300">
                              <span className="inline-flex items-center gap-1">
                                {ownerKind === 'admin' && <span className="text-[10px] font-bold uppercase text-amber-700">👑</span>}
                                {owner?.name}
                              </span>
                            </td>
                            <td className="px-5 py-3 text-sm text-ink-600 dark:text-ink-400">{a.site?.name}</td>
                            <td className="px-5 py-3">
                              <button
                                type="button"
                                onClick={() => cycleStatus(a)}
                                disabled={isToggling}
                                title="ক্লিক করে status বদলান"
                                className={
                                  'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border transition-all ' +
                                  'hover:scale-105 active:scale-95 cursor-pointer select-none ' +
                                  (isToggling ? 'opacity-60 cursor-wait ' : '') +
                                  (a.status === 'present'
                                    ? 'bg-brand-100 text-brand-800 border-brand-200 hover:bg-brand-200 dark:bg-brand-900/40 dark:text-brand-200 dark:border-brand-700'
                                    : a.status === 'absent'
                                    ? 'bg-rose-100 text-rose-800 border-rose-200 hover:bg-rose-200 dark:bg-rose-900/40 dark:text-rose-200 dark:border-rose-700'
                                    : 'bg-amber-100 text-amber-800 border-amber-200 hover:bg-amber-200 dark:bg-amber-900/40 dark:text-amber-200 dark:border-amber-700')
                                }
                              >
                                {isToggling ? (
                                  <HiOutlineRefresh className="w-3 h-3 animate-spin" />
                                ) : (
                                  <span className="text-sm leading-none">{style.icon}</span>
                                )}
                                <span>{style.label}</span>
                              </button>
                            </td>
                            <td className="px-5 py-3 text-sm text-ink-500 dark:text-ink-400">
                              {a.marked_by === 'self' ? '👑 নিজে'
                                : a.marked_by === 'admin' ? '👨‍💼 Admin'
                                : '👷 Worker'}
                            </td>
                            <td className="px-5 py-3 text-right">
                              <button
                                onClick={() => deleteAttendance(a)}
                                className="text-rose-500 hover:text-rose-700 hover:bg-rose-100 dark:hover:bg-rose-900/30 p-1.5 rounded-lg transition-colors"
                                title="মুছে ফেলুন"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Bulk modal ───────────────────────────────────────────────────── */}
      <Modal isOpen={bulkOpen} onClose={() => setBulkOpen(false)} title="বাল্ক হাজিরা — একসাথে অনেক ওয়ার্কার">
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="input-label">সাইট <span className="text-rose-500">*</span></label>
              <select
                value={bulkData.site_id}
                onChange={(e) => setBulkData({ ...bulkData, site_id: e.target.value })}
                className="input-field"
                required
              >
                <option value="">— সাইট —</option>
                {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <label className="input-label">তারিখ <span className="text-rose-500">*</span></label>
              <input
                type="date"
                value={bulkData.date}
                onChange={(e) => setBulkData({ ...bulkData, date: e.target.value })}
                className="input-field"
                required
              />
            </div>
            <div>
              <label className="input-label">Status</label>
              <div className="grid grid-cols-3 gap-1">
                {Object.entries(STATUS_STYLES).map(([key, style]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setBulkData({ ...bulkData, status: key })}
                    className={
                      'px-2 py-2 rounded-lg text-xs font-semibold border transition-colors flex items-center justify-center gap-1 ' +
                      (bulkData.status === key
                        ? key === 'present'
                          ? 'bg-brand-50 border-brand-500 text-brand-700 dark:bg-brand-900/40 dark:text-brand-200'
                          : key === 'absent'
                          ? 'bg-rose-50 border-rose-500 text-rose-700 dark:bg-rose-900/40 dark:text-rose-200'
                          : 'bg-amber-50 border-amber-500 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'
                        : 'bg-white dark:bg-ink-800 border-ink-200 dark:border-ink-700 text-ink-700 dark:text-ink-300')
                    }
                  >
                    <span>{style.icon}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 bg-brand-50 dark:bg-brand-900/20 rounded-xl border border-brand-200 dark:border-brand-800">
            <div className="flex items-center gap-2">
              <HiOutlineCalendar className="w-5 h-5 text-brand-600" />
              <span className="text-sm font-semibold text-ink-900 dark:text-white">
                <span className="text-brand-600 dark:text-brand-300 text-lg">{bulkData.selectedIds.size}</span>
                {' / '}
                {filteredWorkers.length} জন নির্বাচিত
              </span>
            </div>
            <div className="flex gap-2 flex-wrap">
              <label className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-white dark:bg-ink-800 border border-ink-200 dark:border-ink-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={bulkData.includeAdmins}
                  onChange={(e) => setBulkData({ ...bulkData, includeAdmins: e.target.checked })}
                  className="w-3.5 h-3.5 rounded text-amber-600 focus:ring-amber-500"
                />
                <span>👑 অ্যাডমিন অন্তর্ভুক্ত</span>
              </label>
              <button
                type="button"
                onClick={() => selectAll(filteredWorkers.map((w) => w.id))}
                className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-white dark:bg-ink-800 border border-ink-200 dark:border-ink-700 hover:bg-ink-50 dark:hover:bg-ink-700 transition-colors"
              >
                সব নির্বাচন
              </button>
              <button
                type="button"
                onClick={() => deselectAll(filteredWorkers.map((w) => w.id))}
                className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-white dark:bg-ink-800 border border-ink-200 dark:border-ink-700 hover:bg-ink-50 dark:hover:bg-ink-700 transition-colors"
              >
                মুছে দিন
              </button>
            </div>
          </div>

          <div className="relative">
            <input
              type="text"
              value={bulkSearch}
              onChange={(e) => setBulkSearch(e.target.value)}
              placeholder="🔍 নাম বা ফোন খুঁজুন..."
              className="input-field"
            />
            {bulkSearch && (
              <button onClick={() => setBulkSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-400 hover:text-ink-700">
                <HiOutlineX className="w-4 h-4" />
              </button>
            )}
          </div>

          <div className="border border-ink-100 dark:border-ink-700 rounded-xl overflow-hidden">
            <div className="max-h-80 overflow-y-auto divide-y divide-ink-100 dark:divide-ink-700">
              {filteredWorkers.length === 0 ? (
                <div className="p-6 text-center text-sm text-ink-500">
                  {workers.length === 0
                    ? 'কোনো user নেই। প্রথমে Users পেজ থেকে add করুন।'
                    : 'এই search-এ কোনো user পাওয়া যায়নি।'}
                </div>
              ) : (
                filteredWorkers.map((w) => {
                  const isSelected = bulkData.selectedIds.has(w.id);
                  const isAlreadyMarked = alreadyMarked.has(w.id);
                  const isAdmin = w.role === 'admin';
                  return (
                    <label
                      key={w.id}
                      className={
                        'flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors ' +
                        (isSelected
                          ? isAdmin
                            ? 'bg-amber-50 dark:bg-amber-900/30'
                            : 'bg-brand-50 dark:bg-brand-900/30'
                          : 'hover:bg-ink-50 dark:hover:bg-ink-900/30')
                      }
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleWorker(w.id)}
                        className="w-5 h-5 rounded border-2 border-ink-300 text-brand-600 focus:ring-2 focus:ring-brand-500 cursor-pointer"
                      />
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center text-white font-bold flex-shrink-0 text-sm ${isAdmin ? 'bg-gradient-to-br from-amber-500 to-amber-700' : 'bg-gradient-brand'}`}>
                        {w.name?.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="font-medium text-ink-900 dark:text-white text-sm truncate">{w.name}</span>
                          {isAdmin && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-200">
                              👑 Admin
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-ink-500 dark:text-ink-400 truncate">
                          {w.phone || w.email || '-'}
                        </div>
                      </div>
                      {isAlreadyMarked && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200 whitespace-nowrap">
                          <HiOutlineExclamation className="w-3 h-3" />
                          ইতিমধ্যে চিহ্নিত
                        </span>
                      )}
                    </label>
                  );
                })
              )}
            </div>
          </div>

          {bulkResult && (
            <div className="p-4 bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-800 rounded-xl">
              <div className="flex items-start gap-2">
                <HiOutlineCheck className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h3 className="font-semibold text-emerald-800 dark:text-emerald-200 text-sm">{bulkResult.message}</h3>
                  <div className="mt-2 flex flex-wrap gap-3 text-xs">
                    <span className="px-2 py-1 bg-white dark:bg-emerald-900/40 rounded">
                      নতুন: <strong>{bulkResult.created}</strong>
                    </span>
                    <span className="px-2 py-1 bg-white dark:bg-emerald-900/40 rounded">
                      আপডেট: <strong>{bulkResult.updated}</strong>
                    </span>
                    {bulkResult.skipped?.length > 0 && (
                      <span className="px-2 py-1 bg-white dark:bg-amber-900/40 rounded">
                        স্কিপ: <strong>{bulkResult.skipped.length}</strong>
                      </span>
                    )}
                    {bulkResult.failed?.length > 0 && (
                      <span className="px-2 py-1 bg-white dark:bg-rose-900/40 rounded text-rose-700 dark:text-rose-300">
                        ব্যর্থ: <strong>{bulkResult.failed.length}</strong>
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-between items-center pt-2 border-t border-ink-100 dark:border-ink-700">
            <div className="text-xs text-ink-500 dark:text-ink-400">
              Status: <strong className={
                bulkData.status === 'present' ? 'text-brand-600' :
                bulkData.status === 'absent' ? 'text-rose-600' : 'text-amber-600'
              }>{STATUS_STYLES[bulkData.status].icon} {STATUS_STYLES[bulkData.status].label}</strong>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setBulkOpen(false)}
                className="btn-secondary"
              >
                বন্ধ
              </button>
              <button
                type="button"
                onClick={handleBulkSubmit}
                disabled={bulkSubmitting || bulkData.selectedIds.size === 0 || !bulkData.site_id}
                className="btn-primary"
              >
                {bulkSubmitting ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>সংরক্ষণ হচ্ছে...</span>
                  </>
                ) : (
                  <>
                    <HiOutlineCheck className="w-5 h-5" />
                    <span>{bulkData.selectedIds.size} জনের হাজিরা নিন</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AttendancePage;
